import { routeManifest } from "../routes/route-manifest";
import type { AppRouteDefinition } from "../routes/route-types";
import { canGrantRole, getRoleDefinition, isReadOnlyRole, type CoachingRole } from "./roles";

export type AccessPresentation = "render" | "generic-not-found";
export type ActionPresentation = "enabled" | "disabled-read-only" | "disabled-unavailable" | "hidden";
export type RecordScopeStatus = "in-scope" | "out-of-scope" | "unknown";

export interface RouteAccessDecision {
  route: AppRouteDefinition | null;
  allowed: boolean;
  presentation: AccessPresentation;
  reason: string;
  recordScopeStatus?: RecordScopeStatus;
  featureGate?: string;
}

export interface RecordScopeDecisionInput {
  status: RecordScopeStatus;
  reason?: string;
}

export type ActionCapabilityKey =
  | "companies:create"
  | "companies:update"
  | "programs:create"
  | "programs:update"
  | "cohorts:create"
  | "cohorts:update"
  | "cohorts:complete"
  | "participants:create"
  | "participants:update"
  | "participants:bulk-import"
  | "sessions:create"
  | "sessions:create-series"
  | "sessions:send-invites"
  | "sessions:reschedule"
  | "sessions:lifecycle"
  | "sessions:reassign-coach"
  | "sessions:clear-series"
  | "reports:mark-reviewed"
  | "resources:manage"
  | "resources:download"
  | "users:create"
  | "users:update"
  | "permissions:grant-role"
  | "exports:csv"
  | "analytics:view"
  | "profile:update";

export interface ActionCapabilityDefinition {
  key: ActionCapabilityKey;
  label: string;
  allowedRoles: CoachingRole[];
  readOnlyRoles?: CoachingRole[];
  unavailable?: boolean;
  notes: string;
}

export interface ActionCapabilityDecision {
  key: ActionCapabilityKey;
  allowed: boolean;
  presentation: ActionPresentation;
  reason: string;
}

const operationalWriteRoles: CoachingRole[] = ["super_admin", "admin", "coach"];
const adminRoles: CoachingRole[] = ["super_admin", "admin"];
const readOnlyRoles: CoachingRole[] = ["company_admin", "participant"];

export const actionCapabilityDefinitions: ActionCapabilityDefinition[] = [
  action("companies:create", "Create companies", ["super_admin", "admin"], ["company_admin"]),
  action("companies:update", "Update companies", ["super_admin", "admin"], ["company_admin"]),
  action("programs:create", "Create programs", ["super_admin", "admin"], ["company_admin"]),
  action("programs:update", "Update programs", ["super_admin", "admin"], ["company_admin"]),
  action("cohorts:create", "Create cohorts", operationalWriteRoles, readOnlyRoles),
  action("cohorts:update", "Update cohorts", operationalWriteRoles, readOnlyRoles),
  action("cohorts:complete", "Complete cohorts", operationalWriteRoles, readOnlyRoles),
  action("participants:create", "Create participants", operationalWriteRoles, readOnlyRoles),
  action("participants:update", "Update participants", operationalWriteRoles, readOnlyRoles),
  action("participants:bulk-import", "Bulk import participants", operationalWriteRoles, readOnlyRoles),
  action("sessions:create", "Create sessions", operationalWriteRoles, readOnlyRoles),
  action("sessions:create-series", "Create session series", operationalWriteRoles, readOnlyRoles),
  action("sessions:send-invites", "Send invites", operationalWriteRoles, readOnlyRoles),
  action("sessions:reschedule", "Reschedule sessions", operationalWriteRoles, readOnlyRoles),
  // Session lifecycle transitions (complete / cancel / archive). Operational
  // write roles in scope; the backend re-applies the assigned-session scope.
  action("sessions:lifecycle", "Update session lifecycle", operationalWriteRoles, readOnlyRoles),
  action("sessions:reassign-coach", "Reassign coach", adminRoles, ["company_admin", "participant", "cohort_lead"]),
  // Clear Series (FR-SESS-002): the backend endpoint is NOW LIVE
  // (POST /cohorts/:cohort_id/sessions/clear) and coaching.sessions.clear-series.org
  // is granted to super_admin + coach only (migration 0227) — a SA clears any
  // cohort's series, a coach only a cohort assigned to them (the backend enforces
  // in-scope; a foreign cohort 404s-via-400). So `unavailable` is OFF and the
  // capability is enabled for SA + coach. Every other role (admin/company_admin/
  // cohort_lead/participant) is NOT in allowedRoles and is denied (hidden /
  // disabled-read-only), and the page also module-denies participants (fail-closed).
  action("sessions:clear-series", "Clear session series", ["super_admin", "coach"], ["company_admin", "participant", "cohort_lead"]),
  // Phase 4 follow-through (org-admin spec §2.3a): company_admin added to
  // allowedRoles; cohort_lead/participant explicitly read-only; unavailable off.
  action(
    "reports:mark-reviewed",
    "Mark reports reviewed",
    ["super_admin", "admin", "coach", "company_admin"],
    ["cohort_lead", "participant"]
  ),
  action("resources:manage", "Manage resources", adminRoles, readOnlyRoles),
  action("resources:download", "Download resources", ["super_admin", "admin", "coach", "company_admin", "participant", "cohort_lead"]),
  action("users:create", "Create users", adminRoles),
  action("users:update", "Update users", adminRoles),
  action("permissions:grant-role", "Grant roles", ["super_admin"], [], true),
  // Phase 4 follow-through (org-admin spec §2.3b): unavailable off; cohort_lead
  // moved into the explicit read-only deny set (download individual reports, not
  // bulk CSV).
  action("exports:csv", "Export CSV", ["super_admin", "admin", "coach", "company_admin"], ["participant", "cohort_lead"]),
  // Org-admin analytics rollup view (org-admin spec §2.4) — read-only org
  // surface; ungated zero-filled rollup, never a write.
  action("analytics:view", "View org analytics", ["super_admin", "admin", "company_admin"], ["coach", "cohort_lead", "participant"]),
  action("profile:update", "Update allowed profile fields", ["super_admin", "admin", "coach", "company_admin", "participant"])
];

function action(
  key: ActionCapabilityKey,
  label: string,
  allowedRoles: CoachingRole[],
  readOnlyRoles: CoachingRole[] = [],
  unavailable = false
): ActionCapabilityDefinition {
  return {
    key,
    label,
    allowedRoles,
    readOnlyRoles,
    unavailable,
    notes: unavailable ? "Backend/catalog contract is blocked; render disabled with blocker copy." : "Render according to role scope."
  };
}

export function getRouteById(routeId: string) {
  return routeManifest.find((route) => route.id === routeId) ?? null;
}

export function getRouteByPath(pathname: string) {
  return routeManifest.find((route) => routeMatches(route.path, pathname)) ?? null;
}

export function evaluateRouteAccess(role: CoachingRole, routeOrPath: AppRouteDefinition | string, isPlatformAdmin = false): RouteAccessDecision {
  const route = typeof routeOrPath === "string" ? getRouteByPath(routeOrPath) : routeOrPath;
  if (!route) {
    return {
      route: null,
      allowed: false,
      presentation: "generic-not-found",
      reason: "No route contract matches this URL."
    };
  }

  if (route.platformAdminOnly === true && !isPlatformAdmin) {
    return {
      route,
      allowed: false,
      presentation: "generic-not-found",
      reason: `${route.label} is available only to platform administrators; use generic not-found for direct URL privacy.`
    };
  }

  if (!route.roles.includes(role)) {
    return {
      route,
      allowed: false,
      presentation: "generic-not-found",
      reason: `${getRoleDefinition(role).label} cannot access ${route.label}; use generic not-found for direct URL privacy.`
    };
  }

  if (route.readiness === "blocked-backend") {
    return {
      route,
      allowed: true,
      presentation: "render",
      reason: `${route.label} is visible as a no-data blocker until ${route.featureGate ?? "the backend/API contract"} is ready.`,
      recordScopeStatus: "in-scope",
      featureGate: route.featureGate
    };
  }

  return {
    route,
    allowed: true,
    presentation: "render",
    reason: `${getRoleDefinition(role).label} may access ${route.label}.`,
    recordScopeStatus: route.scopeKind && route.scopeKind !== "none" ? "unknown" : "in-scope"
  };
}

export function filterPlatformAdminNavigation(routes: AppRouteDefinition[], isPlatformAdmin: boolean) {
  return routes.filter((route) => route.platformAdminOnly !== true || isPlatformAdmin);
}

export function getNavigationRoutes(role: CoachingRole, isPlatformAdmin = false) {
  const routes = routeManifest.filter((route) => route.nav && route.readiness !== "blocked-backend" && evaluateRouteAccess(role, route, isPlatformAdmin).allowed);
  return filterPlatformAdminNavigation(routes, isPlatformAdmin);
}

export function evaluateScopedRouteAccess(
  role: CoachingRole,
  routeOrPath: AppRouteDefinition | string,
  scopeDecision: RecordScopeDecisionInput
): RouteAccessDecision {
  const baseDecision = evaluateRouteAccess(role, routeOrPath);
  if (!baseDecision.allowed) return baseDecision;

  const route = baseDecision.route;
  if (!route || !route.scopeKind || route.scopeKind === "none") {
    return {
      ...baseDecision,
      recordScopeStatus: "in-scope"
    };
  }

  if (scopeDecision.status === "out-of-scope") {
    return {
      route,
      allowed: false,
      presentation: "generic-not-found",
      recordScopeStatus: "out-of-scope",
      reason:
        scopeDecision.reason ??
        `${getRoleDefinition(role).label} cannot access this ${route.scopeKind} record; use generic not-found for direct URL privacy.`
    };
  }

  if (scopeDecision.status === "unknown") {
    return {
      route,
      allowed: false,
      presentation: "generic-not-found",
      recordScopeStatus: "unknown",
      reason:
        scopeDecision.reason ??
        `Record scope for this ${route.scopeKind} route has not been resolved; do not render record data.`
    };
  }

  return {
    ...baseDecision,
    recordScopeStatus: "in-scope",
    reason: scopeDecision.reason ?? baseDecision.reason
  };
}

export function evaluateActionCapability(role: CoachingRole, key: ActionCapabilityKey): ActionCapabilityDecision {
  const definition = actionCapabilityDefinitions.find((capability) => capability.key === key);
  if (!definition) {
    return {
      key,
      allowed: false,
      presentation: "hidden",
      reason: "No action capability contract exists."
    };
  }

  if (definition.unavailable && definition.allowedRoles.includes(role)) {
    return {
      key,
      allowed: false,
      presentation: "disabled-unavailable",
      reason: `${definition.label} is not available yet.`
    };
  }

  if (definition.allowedRoles.includes(role)) {
    return {
      key,
      allowed: true,
      presentation: "enabled",
      reason: `${getRoleDefinition(role).label} may ${definition.label.toLowerCase()} within scope.`
    };
  }

  if (definition.readOnlyRoles?.includes(role) || isReadOnlyRole(role)) {
    return {
      key,
      allowed: false,
      presentation: "disabled-read-only",
      reason: `${getRoleDefinition(role).label} has read-only access for this workflow.`
    };
  }

  return {
    key,
    allowed: false,
    presentation: "hidden",
    reason: `${getRoleDefinition(role).label} should not see this control.`
  };
}

export function evaluateRoleGrant(granter: CoachingRole, target: CoachingRole): ActionCapabilityDecision {
  return {
    key: "permissions:grant-role",
    allowed: canGrantRole(granter, target),
    presentation: canGrantRole(granter, target) ? "enabled" : "hidden",
    reason: canGrantRole(granter, target)
      ? `${getRoleDefinition(granter).label} may grant ${getRoleDefinition(target).label}.`
      : `${getRoleDefinition(granter).label} may not grant ${getRoleDefinition(target).label}.`
  };
}

function routeMatches(template: string, pathname: string) {
  const templateParts = template.split("/").filter(Boolean);
  const pathParts = pathname.split("?")[0].split("/").filter(Boolean);
  if (templateParts.length !== pathParts.length) return false;
  return templateParts.every((part, index) => part.startsWith(":") || part === pathParts[index]);
}
